// Phase F.2 — Routing rules editor (used inside the master detail panel)
//
// Includes:
//   - Rule list (priority-sorted)
//   - Inline create/edit form for direction + countries + postal prefixes +
//     primary/cc contacts
//   - "Test rule" preview: paste a logistics_request_id, calls
//     POST /api/customer-routing/resolve with trace=true and renders the
//     per-rule evaluation so the operator sees which rule fires first.
import React, { useState } from "react";
import {
  Route as RouteIcon, Plus, Pencil, Trash2, Save, X,
  ChevronUp, FlaskConical, CheckCircle2, XCircle, Loader2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  useRoutingRules, useCreateRule, useUpdateRule, useDeleteRule, useResolveRouting,
} from "../api/customerRouting";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #0a0a0a)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputStyle = {
  background: "#fff",
  border: "1px solid rgba(0,0,0,0.15)",
  color: "#0a0a0a",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: "7px 10px",
  borderRadius: 5,
  width: "100%",
};

const DIR_COLOR = {
  export: "#0a8754", import: "#1769aa", any: "#6b7388",
};

const DirPill = ({ d }) => {
  const c = DIR_COLOR[d] || "#6b7388";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded"
      style={{ background: `${c}1A`, color: c, border: `1px solid ${c}55`, ...mono(10, c, 700, "0.7px"), textTransform: "uppercase" }}
      data-testid={`rule-dir-${d}`}
    >
      {d}
    </span>
  );
};

const _emptyForm = () => ({
  name: "",
  priority: 100,
  predicate: {
    direction: "any",
    pickup_countries: "",
    dropoff_countries: "",
    pickup_postal_prefixes: "",
    dropoff_postal_prefixes: "",
  },
  primary_contact_id: "",
  cc_contact_ids: [],
  is_active: true,
});

const _csvList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

/* ─── Test-rule preview panel ─────────────────────────────────────── */

const TestRulePreview = ({ masterId }) => {
  const resolve = useResolveRouting();
  const [rid, setRid] = useState("");
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!rid) return;
    try {
      const res = await resolve.mutateAsync({
        logistics_request_id: rid.trim(),
        customer_master_id: masterId,
      });
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
      setResult(null);
    }
  };

  return (
    <div
      className="rounded p-3 mb-4"
      style={{ background: "rgba(126,58,168,0.05)", border: "1px solid rgba(126,58,168,0.30)" }}
      data-testid="test-rule-preview"
    >
      <div className="flex items-center gap-2 mb-2">
        <FlaskConical size={14} color="#7e3aa8" />
        <strong style={mono(12, "#7e3aa8", 700, "0.8px")}>TEST RULE</strong>
        <span style={mono(10, "#6b7388")}>— paste a deal ID, see which rule would fire</span>
      </div>
      <div className="flex gap-2">
        <input
          value={rid}
          onChange={(e) => setRid(e.target.value)}
          placeholder="LR-2026-06-ABC123"
          style={inputStyle}
          data-testid="test-rule-input"
        />
        <button
          type="button"
          onClick={run}
          disabled={!rid || resolve.isPending}
          style={{ ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase", background: "#7e3aa8", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", opacity: (!rid || resolve.isPending) ? 0.6 : 1, whiteSpace: "nowrap" }}
          data-testid="test-rule-run"
        >
          {resolve.isPending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : <FlaskConical size={12} className="inline mr-1" />}
          Resolve
        </button>
      </div>
      {result && (
        <div className="mt-3" data-testid="test-rule-result">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div style={mono(11)}><strong>Direction:</strong> <DirPill d={result.facts?.direction} /></div>
            <div style={mono(11)}><strong>Lane:</strong> {result.facts?.pickup_country || "?"} → {result.facts?.dropoff_country || "?"}</div>
            <div style={mono(11)}><strong>Pickup postal:</strong> {result.facts?.pickup_postal || "—"}</div>
            <div style={mono(11)}><strong>Dropoff postal:</strong> {result.facts?.dropoff_postal || "—"}</div>
          </div>
          {result.no_master ? (
            <div className="rounded p-2 mt-2" style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.40)", color: "#aa6a00", ...mono(11) }}>
              No customer-master matches this deal&apos;s customer tenant. Manual contact pick will be required.
            </div>
          ) : result.matched_rule_id ? (
            <div className="rounded p-2 mt-2" style={{ background: "rgba(78,208,132,0.10)", border: "1px solid rgba(78,208,132,0.40)", ...mono(11, "#1c6b3d") }} data-testid="test-rule-matched">
              <CheckCircle2 size={13} className="inline mr-1" />
              <strong>{result.matched_rule_name}</strong> wins. Primary: <strong>{result.primary?.name || "—"}</strong>{result.cc?.length ? `, CC: ${result.cc.map((c) => c.name).join(", ")}` : ""}.
            </div>
          ) : (
            <div className="rounded p-2 mt-2" style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.40)", color: "#aa6a00", ...mono(11) }} data-testid="test-rule-no-match">
              No rule matched. Send-out dialog will require a manual contact pick.
            </div>
          )}
          {(result.trace || []).length > 0 && (
            <div className="mt-3">
              <div style={mono(9, "#6b7388", 700, "1px")}>TRACE</div>
              <ol className="mt-1 space-y-1">
                {result.trace.map((t) => (
                  <li key={t.rule_id} style={mono(11, t.matched ? "#1c6b3d" : "#6b7388")} data-testid={`trace-${t.rule_id}`}>
                    {t.matched
                      ? <CheckCircle2 size={11} className="inline mr-1" />
                      : <XCircle size={11} className="inline mr-1" />}
                    P{t.priority} <strong>{t.name}</strong>
                    {!t.matched && t.failed_matchers?.length > 0
                      ? <span style={mono(10, "#6b7388")}> — failed: {t.failed_matchers.join(", ")}</span>
                      : t.matched ? <span style={mono(10, "#1c6b3d")}> — match</span> : null}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Rule form (create + edit) ───────────────────────────────────── */

const RuleForm = ({ masterId, contacts, initial, onClose }) => {
  const create = useCreateRule();
  const update = useUpdateRule();
  const [form, setForm] = useState(() => {
    if (!initial) return _emptyForm();
    return {
      ...initial,
      predicate: {
        direction: initial.predicate?.direction || "any",
        pickup_countries: (initial.predicate?.pickup_countries || []).join(", "),
        dropoff_countries: (initial.predicate?.dropoff_countries || []).join(", "),
        pickup_postal_prefixes: (initial.predicate?.pickup_postal_prefixes || []).join(", "),
        dropoff_postal_prefixes: (initial.predicate?.dropoff_postal_prefixes || []).join(", "),
      },
      cc_contact_ids: initial.cc_contact_ids || [],
    };
  });
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    const payload = {
      name: form.name,
      priority: Number(form.priority) || 100,
      predicate: {
        direction: form.predicate.direction,
        pickup_countries: _csvList(form.predicate.pickup_countries).map((s) => s.toUpperCase()),
        dropoff_countries: _csvList(form.predicate.dropoff_countries).map((s) => s.toUpperCase()),
        pickup_postal_prefixes: _csvList(form.predicate.pickup_postal_prefixes),
        dropoff_postal_prefixes: _csvList(form.predicate.dropoff_postal_prefixes),
        lanes: [],
      },
      primary_contact_id: form.primary_contact_id || null,
      cc_contact_ids: form.cc_contact_ids,
      is_active: form.is_active,
    };
    try {
      if (initial?.id) {
        await update.mutateAsync({ rule_id: initial.id, ...payload });
        toast.success("Rule updated");
      } else {
        await create.mutateAsync({ master_id: masterId, ...payload });
        toast.success("Rule created");
      }
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail?.message || e2?.response?.data?.detail || e2.message);
    }
  };

  const toggleCc = (cid) => {
    setForm((f) => ({
      ...f,
      cc_contact_ids: f.cc_contact_ids.includes(cid)
        ? f.cc_contact_ids.filter((x) => x !== cid)
        : [...f.cc_contact_ids, cid],
    }));
  };

  return (
    <form onSubmit={submit} className="rounded p-3 mb-3" style={{ background: "rgba(23,105,170,0.05)", border: "1px solid rgba(23,105,170,0.30)" }} data-testid="rule-form">
      <div className="flex items-center justify-between mb-2">
        <strong style={mono(12, "#1769aa", 700, "0.6px")}>{initial?.id ? "EDIT RULE" : "NEW RULE"}</strong>
        <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6b7388" }}><X size={14} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <label className="block col-span-2">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Name *</div>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={inputStyle} data-testid="rule-name" />
        </label>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Priority</div>
          <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} min={0} max={10000} style={inputStyle} data-testid="rule-priority" />
        </label>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Direction</div>
          <select value={form.predicate.direction} onChange={(e) => setForm({ ...form, predicate: { ...form.predicate, direction: e.target.value } })} style={inputStyle} data-testid="rule-direction">
            <option value="any">Any</option>
            <option value="export">Export</option>
            <option value="import">Import</option>
          </select>
        </label>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Pickup countries (CSV)</div>
          <input value={form.predicate.pickup_countries} onChange={(e) => setForm({ ...form, predicate: { ...form.predicate, pickup_countries: e.target.value } })} placeholder="DE, AT" style={inputStyle} data-testid="rule-pickup-countries" />
        </label>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Dropoff countries (CSV)</div>
          <input value={form.predicate.dropoff_countries} onChange={(e) => setForm({ ...form, predicate: { ...form.predicate, dropoff_countries: e.target.value } })} placeholder="HU, RO" style={inputStyle} data-testid="rule-dropoff-countries" />
        </label>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Pickup postal prefixes</div>
          <input value={form.predicate.pickup_postal_prefixes} onChange={(e) => setForm({ ...form, predicate: { ...form.predicate, pickup_postal_prefixes: e.target.value } })} placeholder="803, 90" style={inputStyle} data-testid="rule-pickup-postal" />
        </label>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Dropoff postal prefixes</div>
          <input value={form.predicate.dropoff_postal_prefixes} onChange={(e) => setForm({ ...form, predicate: { ...form.predicate, dropoff_postal_prefixes: e.target.value } })} placeholder="10, 11" style={inputStyle} data-testid="rule-dropoff-postal" />
        </label>
      </div>
      <label className="block mb-2">
        <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Primary contact</div>
        <select value={form.primary_contact_id} onChange={(e) => setForm({ ...form, primary_contact_id: e.target.value })} style={inputStyle} data-testid="rule-primary-contact">
          <option value="">— none —</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
        </select>
      </label>
      <div className="mb-2">
        <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>CC contacts</div>
        <div className="flex flex-wrap gap-2">
          {contacts.map((c) => (
            <label key={c.id} className="inline-flex items-center gap-1" style={mono(11)}>
              <input type="checkbox" checked={form.cc_contact_ids.includes(c.id)} onChange={() => toggleCc(c.id)} data-testid={`rule-cc-${c.id}`} />
              {c.name}
            </label>
          ))}
        </div>
      </div>
      {err && (
        <div className="rounded px-3 py-2 inline-flex items-center gap-2 mb-2"
             style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.40)", color: "#b3261e", ...mono(11), width: "100%" }}
             data-testid="rule-form-error">
          <AlertCircle size={13} /> {String(err)}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} style={{ ...mono(10, "#6b7388"), background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "6px 12px", borderRadius: 4, cursor: "pointer" }}>Cancel</button>
        <button type="submit" disabled={create.isPending || update.isPending || !form.name}
                style={{ ...mono(10, "#fff", 700, "0.8px"), textTransform: "uppercase", background: "#1769aa", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 4, cursor: "pointer", opacity: (create.isPending || update.isPending || !form.name) ? 0.6 : 1 }}
                data-testid="rule-form-save">
          {(create.isPending || update.isPending) ? <Loader2 size={11} className="animate-spin inline mr-1" /> : <Save size={11} className="inline mr-1" />}
          {initial?.id ? "Save" : "Create"}
        </button>
      </div>
    </form>
  );
};

/* ─── Rule row ─────────────────────────────────────────────────────── */

const RuleRow = ({ rule, contacts, onEdit }) => {
  const del = useDeleteRule();
  const primary = contacts.find((c) => c.id === rule.primary_contact_id);
  const ccNames = (rule.cc_contact_ids || [])
    .map((cid) => contacts.find((c) => c.id === cid)?.name)
    .filter(Boolean);

  const remove = async () => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try { await del.mutateAsync(rule.id); toast.success("Rule deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e.message); }
  };

  return (
    <div className="rounded p-3 mb-2 flex items-start gap-3" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.10)" }} data-testid={`rule-row-${rule.id}`}>
      <span style={{ ...mono(11, "#6b7388", 700, "0.8px"), background: "rgba(0,0,0,0.05)", padding: "3px 7px", borderRadius: 4, minWidth: 36, textAlign: "center" }}>
        P{rule.priority}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <strong style={mono(12)}>{rule.name}</strong>
          <DirPill d={rule.predicate?.direction || "any"} />
        </div>
        <div style={mono(10, "#6b7388")} className="space-x-2">
          {rule.predicate?.pickup_countries?.length > 0 && <span>PU: {rule.predicate.pickup_countries.join("/")}</span>}
          {rule.predicate?.dropoff_countries?.length > 0 && <span>DO: {rule.predicate.dropoff_countries.join("/")}</span>}
          {rule.predicate?.pickup_postal_prefixes?.length > 0 && <span>PU postal: {rule.predicate.pickup_postal_prefixes.join("/")}*</span>}
          {rule.predicate?.dropoff_postal_prefixes?.length > 0 && <span>DO postal: {rule.predicate.dropoff_postal_prefixes.join("/")}*</span>}
        </div>
        <div className="mt-1" style={mono(11)}>
          <strong>→</strong> {primary ? primary.name : <em style={mono(11, "#cbd5e1")}>no primary</em>}
          {ccNames.length > 0 && <span style={mono(10, "#6b7388")}> · CC: {ccNames.join(", ")}</span>}
        </div>
      </div>
      <div className="flex gap-1">
        <button onClick={() => onEdit(rule)} style={{ background: "transparent", border: "none", color: "#6b7388", cursor: "pointer", padding: 4 }} data-testid={`rule-edit-btn-${rule.id}`}><Pencil size={13} /></button>
        <button onClick={remove} style={{ background: "transparent", border: "none", color: "#b3261e", cursor: "pointer", padding: 4 }} data-testid={`rule-delete-btn-${rule.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
};

/* ─── Top-level editor ─────────────────────────────────────────────── */

export const RoutingRulesEditor = ({ masterId, contacts }) => {
  const { data, isLoading } = useRoutingRules(masterId);
  const [editing, setEditing] = useState(null); // null | "new" | rule object
  const items = data?.items || [];

  return (
    <section className="border-t pt-4 mt-4" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="routing-rules-editor">
      <div className="flex items-center justify-between mb-3">
        <h3 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14 }}>
          <RouteIcon size={14} className="inline mr-1" />
          Routing rules ({items.length})
        </h3>
        {!editing && (
          <button onClick={() => setEditing("new")} style={{ ...mono(10, "#1769aa", 700, "0.6px"), textTransform: "uppercase", background: "rgba(23,105,170,0.10)", border: "1px solid rgba(23,105,170,0.30)", padding: "5px 10px", borderRadius: 5, cursor: "pointer" }} data-testid="add-rule-btn">
            <Plus size={11} className="inline mr-1" /> Add rule
          </button>
        )}
      </div>

      <TestRulePreview masterId={masterId} />

      {editing && (
        <RuleForm
          masterId={masterId}
          contacts={contacts}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {isLoading && <div style={mono(11, "#6b7388")}>Loading rules…</div>}
      {!isLoading && items.length === 0 && !editing && (
        <div className="text-center py-4" style={mono(11, "#6b7388")} data-testid="rules-empty">
          No routing rules yet. Add one to auto-suggest contacts on send-out.
        </div>
      )}
      {items.map((r) => (
        <RuleRow key={r.id} rule={r} contacts={contacts} onEdit={(rule) => setEditing(rule)} />
      ))}
    </section>
  );
};

export default RoutingRulesEditor;
